/**
 * App.js - Application entry point and routing
 * Handles navigation, authentication gate, and page routing
 */
import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router';
import { Upload, Map, ClipboardCheck, ClipboardList, Palette, Settings, Inbox, LayoutDashboard, LogOut, Truck, WifiOff } from 'lucide-react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import {
  IncomingOrdersProvider,
  useIncomingOrders,
} from '@/context/IncomingOrdersContext';
import { ShopSettingsProvider } from '@/context/ShopSettingsContext';
import { getActiveSessionRevision } from '@/api/authenticatedClient';
import { confirmCommunityNavigation } from '@/utils/communityNavigationGuard';
import useReviewCount from '@/hooks/useReviewCount';
import NotificationBell from '@/components/NotificationBell';
import DesktopUpdateBar from '@/components/DesktopUpdateBar';
import DesktopAuthorizationNotice from '@/components/DesktopAuthorizationNotice';
import DesktopLocalStoreNotice from '@/components/DesktopLocalStoreNotice';
import DesktopLocalRuntimeNotice from '@/components/DesktopLocalRuntimeNotice';
import DesktopPortableRecoveryNotice from '@/components/DesktopPortableRecoveryNotice';
import DesktopOfflineReadinessNotice from '@/components/DesktopOfflineReadinessNotice';
import DesktopOrderSync from '@/components/DesktopOrderSync';
import DesktopOfflineMapUpdateNotice from '@/components/DesktopOfflineMapUpdateNotice';
import RoutingDataUpdateNotice from '@/components/RoutingDataUpdateNotice';
import LoginPage from '@/views/LoginPage';
import PodReceiptPage from '@/views/PodReceiptPage';
import DashboardView from './views/DashboardView';
import UploadView from './views/UploadView';
import MapView from './views/MapView';
import DeliverView from './views/DeliverView';
import AllOrdersView from './views/AllOrdersView';
import DesignView from './views/DesignView';
import IncomingView from './views/IncomingView';
import SettingsView from './views/SettingsView';
import ReconciliationView from './views/ReconciliationView';
import DriverRunsView from './views/DriverRunsView';
import { isDesktopApp, isDesktopOffline } from '@/api/desktopSync';
import useDesktopDeviceStatus, {
  canCaptureOfflineOrders,
} from '@/hooks/useDesktopDeviceStatus';
import {
  canOpenAuthenticatedPath,
  defaultAuthenticatedPath,
  isAuthenticatedPathAvailable,
  isAuthenticatedPathVisible,
  normalizedUserRoles,
} from '@/utils/roleLanding';

/**
 * Navigation component - Tab-based navigation with role-based visibility
 */
const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const review = useReviewCount(location.pathname);
  const offline = isDesktopOffline();
  const deviceStatus = useDesktopDeviceStatus();

  const roles = normalizedUserRoles(user);
  const hasAdminRole = roles.includes('admin');
  const canCaptureOffline = canCaptureOfflineOrders(roles, deviceStatus);
  const screenContext = { desktop: isDesktopApp(), offline, canCaptureOffline };
  const { orders: incomingOrders } = useIncomingOrders();
  const incomingCount = incomingOrders.length;

  // Display metadata stays here; screen permission lives in roleLanding.
  const tabs = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/incoming', icon: Inbox, label: 'Incoming', badge: incomingCount },
    { path: '/upload', icon: Upload, label: 'Upload' },
    { path: '/orders', icon: ClipboardList, label: 'All Orders' },
    { path: '/design', icon: Palette, label: 'Design' },
    { path: '/dispatch', icon: Map, label: 'Deliver' },
    { path: '/reconciliation', icon: ClipboardCheck, label: 'Review', badge: review.count },
    { path: '/my-runs', icon: Truck, label: 'My Runs' },
  ].filter(({ path }) => isAuthenticatedPathVisible(user, path, screenContext));
  
  const isSettings = location.pathname === '/settings';
  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };
  
  return (
    <nav className={`${offline ? 'bg-amber-600' : 'bg-white/80 backdrop-blur-md border-b border-stone-200/50'} shadow-soft-sm sticky top-0 z-50`} data-testid="main-navigation">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center p-2">
          {offline && (
            <span
              className="flex items-center gap-1.5 px-3 py-1 mr-2 rounded-lg bg-white/20 text-white text-sm font-semibold flex-shrink-0"
              data-testid="offline-indicator"
            >
              <WifiOff className="h-4 w-4" /> Offline
            </span>
          )}
          {/* Scrollable tabs container - prevents whole page horizontal scroll on mobile */}
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide flex-1 min-w-0">
            {tabs.map(({ path, icon: Icon, label, badge }) => {
              const isActive = location.pathname === path;
              const locked = !isAuthenticatedPathAvailable(path, screenContext);
              const base = 'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0';
              const testId = `nav-${label.toLowerCase().replace(' ', '-')}`;
              if (locked) {
                return (
                  <span
                    key={path}
                    className={`${base} text-white/50 cursor-not-allowed select-none`}
                    data-testid={testId}
                    title={
                      path === '/upload'
                        ? 'Offline order entry is available only on the primary computer.'
                        : 'Unavailable offline'
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </span>
                );
              }
              const activeCls = offline
                ? 'bg-white text-amber-700'
                : 'bg-primary/10 text-primary border-b-2 border-primary';
              const inactiveCls = offline
                ? 'text-white/90 hover:bg-white/10'
                : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900';
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={event => {
                    if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey
                        && !confirmCommunityNavigation()) event.preventDefault();
                  }}
                  className={`${base} ${isActive ? activeCls : inactiveCls}`}
                  data-testid={testId}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {path === '/reconciliation' && ['loading', 'unavailable'].includes(review.phase) && (
                    <span className="sr-only">{review.phase === 'loading' ? ' — checking count' : ' — count unavailable'}</span>
                  )}
                  {badge > 0 && (
                    <span className="ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-amber-500 text-white">
                      {badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-1 flex-shrink-0">
            {user && (
              <span className={`text-sm font-medium px-2 hidden sm:block ${offline ? 'text-white/90' : 'text-stone-500'}`}>
                {user.name || user.username}
              </span>
            )}
            {/* In-app notifications for admins (greyed offline — non-functional) */}
            {hasAdminRole && (
              <span className={offline ? 'opacity-50 pointer-events-none' : undefined}>
                <NotificationBell />
              </span>
            )}
            {/* Every authenticated user may read the permission-aware Settings landing page. */}
            {user && (
              <Link
                to="/settings"
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isSettings
                    ? offline ? 'bg-white text-primary' : 'bg-primary/10 text-primary'
                    : offline ? 'text-white/80 hover:bg-white/10' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-700'
                }`}
                data-testid="nav-settings"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
            )}
            <button
              onClick={handleLogout}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${offline ? 'text-white/80 hover:bg-white/10' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-700'}`}
              data-testid="nav-logout-btn"
              title={`Signed in as ${user?.name || user?.username}`}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

/**
 * AuthGate - shows login or app based on auth state
 * Routes users to appropriate views based on their roles
 */
const AuthGate = () => {
  const { user, loading } = useAuth();
  const deviceStatus = useDesktopDeviceStatus();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const roles = normalizedUserRoles(user);
  const offline = isDesktopOffline();
  const desktop = isDesktopApp();
  const canCaptureOffline = canCaptureOfflineOrders(roles, deviceStatus);
  const screenContext = { desktop, offline, canCaptureOffline };
  const landingPath = defaultAuthenticatedPath(user, screenContext);
  const incomingEnabled = roles.includes('admin') && !offline;
  const incomingSessionKey = [
    getActiveSessionRevision(),
    user?.id || user?.username || '',
    [...roles].sort().join('|'),
  ].join(':');
  const protectedView = (path, view) => (
    canOpenAuthenticatedPath(user, path, screenContext)
      ? view : <Navigate to={landingPath} replace />
  );

  return (
    <IncomingOrdersProvider
      enabled={incomingEnabled}
      sessionKey={incomingSessionKey}
    >
      <Routes>
      <Route path="/*" element={
        <>
          <Navigation />
          <DesktopUpdateBar />
          <DesktopAuthorizationNotice />
          <DesktopLocalStoreNotice />
          <DesktopLocalRuntimeNotice />
          <DesktopOfflineReadinessNotice />
          <DesktopOfflineMapUpdateNotice />
          <RoutingDataUpdateNotice />
          <Routes>
            {/* Explicit composition, shared entry/availability rules. */}
            <Route path="/my-runs" element={protectedView('/my-runs', <DriverRunsView />)} />
            <Route path="/driver" element={<Navigate to="/my-runs" replace />} />
            {/* Retain the legacy online root fallback for unsupported roles. */}
            <Route path="/" element={!offline && landingPath === '/' ? <DashboardView /> : <Navigate to={landingPath} replace />} />
            <Route path="/incoming" element={protectedView('/incoming', <IncomingView />)} />
            <Route path="/upload" element={protectedView('/upload', <UploadView />)} />
            <Route path="/orders" element={protectedView('/orders', <AllOrdersView />)} />
            <Route path="/reconciliation" element={protectedView('/reconciliation', <ReconciliationView />)} />
            <Route path="/design" element={protectedView('/design', <DesignView />)} />
            <Route path="/dispatch" element={protectedView('/dispatch', <DeliverView />)} />
            <Route path="/settings" element={protectedView('/settings', <SettingsView />)} />
            <Route path="*" element={<Navigate to={landingPath} replace />} />
          </Routes>
        </>
      } />
      </Routes>
    </IncomingOrdersProvider>
  );
};

/**
 * Main App component
 */
function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <DesktopPortableRecoveryNotice />
          <DesktopOrderSync />
          <ShopSettingsProvider>
          <Routes>
            {/* Public receipt page — no auth required */}
            <Route path="/pod/:orderId" element={<PodReceiptPage />} />
            {/* Everything else goes through auth */}
            <Route path="/*" element={<AuthGate />} />
          </Routes>
          </ShopSettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
